import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { USER_TYPE, IAppUser } from '~/types/app-user'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FORM_ERRORS } from '~/const'
import { getDeviceTypeOptions } from '~/const/options'
import useApps from '~/hooks/useApps'
import { useResendAppsUserVerificationMailMutation } from '~/queries/app-users'

import '../../styles.scss'

interface FormValues {
  appKey: string
  deviceType: string
}

const validationSchema = Yup.object().shape({
  appKey: Yup.string().required(FORM_ERRORS.REQUIRED),
  deviceType: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  email: string
  env: USER_TYPE
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  user: IAppUser
}

const ResendVerificationEmailForm = ({ email, env, onClose, onFailure, onSuccess, user }: FormProps): ReactElement => {
  /** Local state */

  const { appKeys, isAppsLoading } = useApps()

  const appKeyOptions = appKeys.map(({ appKey, label, url }) => ({ label, url, value: appKey }))

  const deviceTypeOptions = getDeviceTypeOptions(['android', 'ios'])

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    watch,
  } = useForm<FormValues>({ resolver: yupResolver(validationSchema) })

  const appKeyWatch = watch('appKey')

  const deviceTypeWatch = watch('deviceType')

  /** Queries & Mutations */

  const { mutateAsync: resendAppsUserVerificationMail } = useResendAppsUserVerificationMailMutation()

  /** Handlers */

  const formatSelectInputValue = (value: IOption | null) => (!value ? value : (value as IOption).value)

  const handleFormSubmit = async ({ appKey, deviceType }: FormValues) => {
    try {
      await resendAppsUserVerificationMail({
        appKey,
        deviceType,
        email,
        env,
        userId: user.user_id,
      })
      onSuccess()
    } catch (e) {
      const apiError = e as AxiosError<IApiError>

      switch (apiError.response?.status) {
        case 409:
          setError('appKey', { message: 'This email was already verified' })
          break
        default:
          onFailure()
      }
    }
  }

  return (
    <>
      <form
        className="resend-apps-user-verification-mail-form modal-form"
        id="resend-apps-user-verification-mail-form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Controller
          control={control}
          name="deviceType"
          render={({ field }) => (
            <SelectInput
              {...field}
              error={errors.deviceType?.message}
              label="Device type"
              onChange={(value) => {
                field.onChange(formatSelectInputValue(value as IOption | null))
              }}
              options={deviceTypeOptions}
              required
              value={deviceTypeOptions.find(({ value }) => value === deviceTypeWatch)}
            />
          )}
        />
        <Controller
          control={control}
          name="appKey"
          render={({ field }) => (
            <SelectInput
              {...field}
              components={{ Option: CustomAppOption }}
              error={errors.appKey?.message}
              isLoading={isAppsLoading}
              label="Application"
              onChange={(value) => {
                field.onChange(formatSelectInputValue(value as IOption | null))
              }}
              options={appKeyOptions}
              required
              value={appKeyOptions.find(({ value }) => value === appKeyWatch)}
            />
          )}
        />
      </form>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} form="resend-apps-user-verification-mail-form" type="submit">
          {isSubmitting ? 'Loading...' : 'Send'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ResendVerificationMailModal = ({
  email,
  env,
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  user,
}: ModalProps): ReactElement => {
  return (
    <Modal className="resend-apps-user-verification-mail-modal apps-users-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Resend verification mail</ModalHeader>
      <ResendVerificationEmailForm
        email={email}
        env={env}
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        user={user}
      />
    </Modal>
  )
}

export default ResendVerificationMailModal
